import { Button, Card, Group, Tabs, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconPlus } from '@tabler/icons-react';
import type { CreateReasonBody } from 'api/models';
import { ReasonForm } from 'app/Private/Reasons/ReasonForm/ReasonForm';
import { PageTitle } from 'components/PageTitle';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { TaskTypeForm, useCreateTaskType, useLoadTaskTypes } from 'modules/Tasks';
import { useState } from 'react';
import { useHistory } from 'react-router';
import EditCustomVariable from 'shared/action-component/EditCustomVariable';
import { ReasonTypeSchema, useCreateReason } from '~/modules/Settings/Variables/Reasons/api';
import { CustomVariables } from './CustomVariables/CustomVariables';
import { Reasons } from './Reasons/Reasons';
import { TaskTypes } from './TaskTypes/TaskTypes';

export const Variables = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const company = useCompanyConfiguration();

  const { can_use_tasks, can_use_custom_variables } = company.package;

  const { refetchTaskTypes } = useLoadTaskTypes();

  const { createTaskType } = useCreateTaskType();
  const { createReason } = useCreateReason();

  const addTaskTypeLabel = t(i18nKeys.ADD_TASK_TYPE);
  const addReasonLabel = t(i18nKeys.ADD_REASON);
  const newCustomVariableLabel = t(i18nKeys.FORM.CUSTOM_VARIABLES.TITLE.NEW);

  const openCustomVariableModal = () =>
    modals.open({
      size: 'lg',
      title: newCustomVariableLabel,
      children: <EditCustomVariable onClose={() => modals.closeAll()} />,
    });

  const openTaskTypeModal = () =>
    modals.open({
      size: 'sm',
      title: addTaskTypeLabel,
      children: (
        <TaskTypeForm
          onCancel={() => modals.closeAll()}
          onSubmit={data =>
            createTaskType(data, {
              onSuccess: () => {
                refetchTaskTypes();
                modals.closeAll();
              },
            })
          }
        />
      ),
    });

  const openReasonModal = () =>
    modals.open({
      size: 'sm',
      title: addReasonLabel,
      children: (
        <ReasonForm
          onCancel={() => modals.closeAll()}
          onSubmit={(data: CreateReasonBody) =>
            createReason(
              { name: data.name, reasonType: ReasonTypeSchema.parse(data.reason_type) },
              {
                onSuccess: () => modals.closeAll(),
              },
            )
          }
        />
      ),
    });

  const tabItems = [
    {
      title: t(i18nKeys.SETTINGS.CUSTOMIZATION.TABS.CUSTOM_VARIABLES),
      actionLabel: newCustomVariableLabel,
      path: '/settings/variables/custom-variables',
      active: can_use_custom_variables,
      children: (
        <CustomVariables
          onCreate={openCustomVariableModal}
          actionLabel={newCustomVariableLabel}
          isActive={can_use_custom_variables}
        />
      ),
      action: openCustomVariableModal,
    },
    {
      title: t(i18nKeys.SETTINGS.CUSTOMIZATION.TABS.TASK_TYPES),
      actionLabel: addTaskTypeLabel,
      path: '/settings/variables/task-types',
      active: can_use_tasks,
      children: (
        <TaskTypes
          onCreate={openTaskTypeModal}
          actionLabel={addTaskTypeLabel}
          isActive={can_use_tasks}
        />
      ),
      action: openTaskTypeModal,
    },
    {
      title: t(i18nKeys.SETTINGS.CUSTOMIZATION.TABS.REASONS),
      actionLabel: addReasonLabel,
      path: '/settings/variables/reasons',
      active: true,
      children: <Reasons onCreate={openReasonModal} actionLabel={addReasonLabel} isActive />,
      action: openReasonModal,
    },
  ];

  const [activePath, setActivePath] = useState(
    tabItems.find(item => item.path === location.pathname)?.path ?? tabItems[0].path,
  );
  const activeItem = tabItems.find(item => item.path === activePath) ?? tabItems[0];

  return (
    <>
      <PageTitle>
        {t(i18nKeys.NAV.CUSTOMIZATION)}
        <PageTitle.Actions>
          <Button
            disabled={!activeItem.active}
            rightSection={<IconPlus size={20} stroke={1.5} />}
            onClick={() => activeItem.active && activeItem.action()}
          >
            {activeItem.actionLabel}
          </Button>
        </PageTitle.Actions>
      </PageTitle>
      <Tabs
        value={activePath}
        onChange={value => {
          if (!value) return;
          setActivePath(value);
          history.push(value);
        }}
      >
        <Group justify="space-between" align="center">
          <Tabs.List w="100%">
            {tabItems.map(({ path, title }) => (
              <Tabs.Tab key={path} value={path}>
                {title}
              </Tabs.Tab>
            ))}
          </Tabs.List>
          <Text size="sm" c="dimmed">
            {!activeItem.active && t(i18nKeys.NOT_INCLUDED)}
          </Text>
        </Group>
        {tabItems.map(({ path, children }) => (
          <Tabs.Panel key={path} value={path} pt="md">
            <Card radius={8} p={0}>
              {children}
            </Card>
          </Tabs.Panel>
        ))}
      </Tabs>
    </>
  );
};
